import { useCallback, useEffect, useState } from 'react';
import { ArchiveRestore, Plus, UserMinus, UserPlus, Users } from 'lucide-react';
import type { Me, RosterRow } from '../api/auth';
import { rosterApi } from '../api/auth';
import { ValidationFailure } from '../api/events';
import { subgroupsApi } from '../api/subgroups';
import type { Subgroup } from '../api/subgroups';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { TextField } from '../ui/Field';

/**
 * How a unit is organised: its patrols, or its dens, or whatever it calls them.
 *
 * **Every noun on this screen comes from the unit.** `labelSingular` and `labelPlural` are read
 * from the membership, so a Troop says "Patrol" and a Pack says "Den" with no branch in the code
 * and no second screen. That is the whole reason those two columns exist on `Unit` — they have
 * been populated and surfaced since the first migration and, until now, consumed by nothing.
 */
export function SubgroupsScreen({
  unitId,
  unitName,
  labelSingular,
  labelPlural,
  canManage,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  labelSingular: string;
  labelPlural: string;
  /** Officer and above. The API is the enforcement point; this decides what renders. */
  canManage: boolean;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [subgroups, setSubgroups] = useState<Subgroup[] | null>(null);
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [groups, people] = await Promise.all([
        subgroupsApi.list(unitId),
        rosterApi.list(unitId),
      ]);
      setSubgroups(groups);
      setRoster(people);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not load the unit's ${labelPlural}.`);
    }
  }, [unitId, labelPlural]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setNameError(null);
    setError(null);
    try {
      await work();
      await load();
    } catch (e) {
      if (e instanceof ValidationFailure) {
        setNameError(e.errors.name?.[0] ?? e.errors.personId?.[0] ?? e.message);
      } else {
        setError(e instanceof Error ? e.message : 'That did not work.');
      }
    } finally {
      setBusy(false);
    }
  }

  const live = subgroups?.filter((sg) => !sg.isRetired) ?? [];
  const retired = subgroups?.filter((sg) => sg.isRetired) ?? [];

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-3)' }}
            >
              {labelPlural}
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '52ch' }}
            >
              {canManage
                ? `A ${labelSingular.toLowerCase()} can have its own thread, which arrives with the unit's adult leadership already monitoring it.`
                : // Not a disabled control: a member is not waiting on a permission that might
                  // arrive, so the screen says who acts instead.
                  `Your unit's officers organise its ${labelPlural.toLowerCase()}.`}
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)', maxWidth: '44rem' }}>
            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-4)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {error}
              </p>
            )}

            {canManage && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void act(async () => {
                    await subgroupsApi.create(unitId, newName.trim());
                    setNewName('');
                  });
                }}
                className="flex items-end flex-wrap gap-3"
                style={{ marginBottom: 'var(--space-6)' }}
              >
                <div style={{ flex: '1 1 16rem' }}>
                  <TextField
                    label={`New ${labelSingular.toLowerCase()}`}
                    required
                    value={newName}
                    onChange={(e) => setNewName(e.currentTarget.value)}
                    error={nameError ?? undefined}
                    placeholder="Flaming Arrow"
                  />
                </div>
                <Button
                  variant="primary"
                  type="submit"
                  disabledReason={
                    !newName.trim() ? 'Give it the name the unit already uses.' : undefined
                  }
                >
                  <Plus size={16} aria-hidden="true" />
                  {busy ? 'Working…' : 'Add'}
                </Button>
              </form>
            )}

            {subgroups && live.length === 0 && retired.length === 0 && (
              <EmptyState
                icon={Users}
                headline={`No ${labelPlural.toLowerCase()} yet`}
                explanation={`Organise the unit into ${labelPlural.toLowerCase()} and each one can have a thread of its own, with the unit's leaders already in it.`}
              />
            )}

            <ul className="flex flex-col" style={{ gap: 'var(--space-4)', listStyle: 'none', margin: 0, padding: 0 }}>
              {live.map((subgroup) => (
                <li key={subgroup.id}>
                  <SubgroupCard
                    subgroup={subgroup}
                    roster={roster}
                    labelSingular={labelSingular}
                    canManage={canManage}
                    busy={busy}
                    onAssign={(personId) =>
                      void act(() => subgroupsApi.assign(unitId, subgroup.id, personId))
                    }
                    onUnassign={(personId) =>
                      void act(() => subgroupsApi.unassign(unitId, subgroup.id, personId))
                    }
                    onRetire={() => void act(() => subgroupsApi.setRetired(unitId, subgroup.id, true))}
                  />
                </li>
              ))}
            </ul>

            {retired.length > 0 && (
              <div style={{ marginTop: 'var(--space-7)' }}>
                <h2 className="t-label" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                  Retired
                </h2>
                <p
                  className="t-caption"
                  style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 var(--space-3)', maxWidth: '52ch' }}
                >
                  Kept rather than deleted, so their threads stay readable and &ldquo;who was in
                  this last year&rdquo; still has an answer.
                </p>

                <ul className="flex flex-col" style={{ gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
                  {retired.map((subgroup) => (
                    <li
                      key={subgroup.id}
                      className="flex items-center justify-between gap-3"
                      style={{
                        background: 'var(--color-surface-raised)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--space-3) var(--space-4)',
                        minHeight: 'var(--tap-min)',
                      }}
                    >
                      <span className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
                        {subgroup.name}
                      </span>
                      {canManage && (
                        <button
                          type="button"
                          className="link-button t-caption"
                          onClick={() => void act(() => subgroupsApi.setRetired(unitId, subgroup.id, false))}
                        >
                          <span className="inline-flex items-center gap-2">
                            <ArchiveRestore size={14} aria-hidden="true" />
                            Bring it back
                          </span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

function SubgroupCard({
  subgroup,
  roster,
  labelSingular,
  canManage,
  busy,
  onAssign,
  onUnassign,
  onRetire,
}: {
  subgroup: Subgroup;
  roster: RosterRow[];
  labelSingular: string;
  canManage: boolean;
  busy: boolean;
  onAssign: (personId: string) => void;
  onUnassign: (personId: string) => void;
  onRetire: () => void;
}) {
  const [adding, setAdding] = useState('');

  const inThisOne = new Set(subgroup.members.map((m) => m.personId));

  // Only people on the unit's roster, and only those not already in this one. Guardians are
  // excluded: they hold no membership, so there is nothing for an assignment to hang off — the
  // API would refuse it, and offering the name would be an invitation to a dead end.
  const available = roster.filter(
    (r) => !r.isGuardianOnly && r.permissionLevel !== 'None' && !inThisOne.has(r.personId),
  );

  return (
    <article
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="t-subheading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {subgroup.name}
        </h2>
        <span className="t-caption tabular" style={{ color: 'var(--color-text-muted)' }}>
          {subgroup.members.length}
        </span>
      </div>

      {subgroup.members.length === 0 ? (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-3) 0 0' }}>
          Nobody in it yet.
        </p>
      ) : (
        <ul
          className="flex flex-col"
          style={{ gap: 'var(--space-1)', listStyle: 'none', margin: 'var(--space-3) 0 0', padding: 0 }}
        >
          {subgroup.members.map((member) => (
            <li
              key={member.personId}
              className="flex items-center justify-between gap-3"
              style={{ minHeight: canManage ? 'var(--tap-min)' : undefined }}
            >
              <span className="t-body" style={{ color: 'var(--color-text-primary)' }}>
                {member.displayName}
                {member.positionTitle && (
                  <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}&middot; {member.positionTitle}
                  </span>
                )}
              </span>

              {canManage && (
                <button
                  type="button"
                  className="link-button t-caption"
                  onClick={() => onUnassign(member.personId)}
                  aria-label={`Take ${member.displayName} out of ${subgroup.name}`}
                >
                  <span className="inline-flex items-center gap-1">
                    <UserMinus size={14} aria-hidden="true" />
                    Remove
                  </span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 'var(--space-4)' }}>
          <label className="sr-only" htmlFor={`add-${subgroup.id}`}>
            Add somebody to {subgroup.name}
          </label>
          <select
            id={`add-${subgroup.id}`}
            value={adding}
            onChange={(e) => setAdding(e.currentTarget.value)}
            style={{
              minHeight: '44px',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1.5px solid var(--color-border-strong)',
              background: 'var(--color-surface-raised)',
              color: 'var(--color-text-primary)',
              font: 'var(--text-body)',
              flex: '1 1 12rem',
            }}
          >
            <option value="">Add somebody…</option>
            {available.map((person) => (
              <option key={person.personId} value={person.personId}>
                {person.displayName}
              </option>
            ))}
          </select>

          <Button
            variant="secondary"
            disabledReason={!adding ? 'Pick somebody first.' : busy ? 'Working…' : undefined}
            onClick={() => {
              onAssign(adding);
              setAdding('');
            }}
          >
            <UserPlus size={16} aria-hidden="true" />
            Add
          </Button>

          <button
            type="button"
            className="link-button t-caption"
            onClick={onRetire}
            style={{ marginLeft: 'auto' }}
          >
            Retire this {labelSingular.toLowerCase()}
          </button>
        </div>
      )}
    </article>
  );
}
